import { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import Index from '@/pages/Index';
import Success from '@/pages/Success';
import Demo from '@/pages/Demo';
import BrandSetup from '@/pages/BrandSetup';
import LanguageInitializer from '@/components/LanguageInitializer';
import { captureEvent } from '@/utils/analytics';

function generateSessionId() {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

function App() {
  useEffect(() => {
    // Session tracking
    const sessionId = generateSessionId();
    captureEvent('session_start', { session_id: sessionId });

    const handleBeforeUnload = () => {
      captureEvent('session_end', { session_id: sessionId });
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Mouse hover tracking
    let hoverTimeout: number | null = null;
    let lastHoveredElement: Element | null = null;

    const handleMouseOver = (e: MouseEvent) => {
      const target = e.target as Element;
      if (
        target &&
        target.hasAttribute('data-track-hover') &&
        target !== lastHoveredElement
      ) {
        lastHoveredElement = target;
        hoverTimeout = window.setTimeout(() => {
          const rect = target.getBoundingClientRect();
          captureEvent('mouse_hover', {
            session_id: sessionId,
            tag: target.tagName,
            id: target.id,
            class: target.className,
            x: e.clientX,
            y: e.clientY,
            width: rect.width,
            height: rect.height,
            timestamp: new Date().toISOString(),
          });
        }, 100); // Only track if hovered for >100ms
      }
    };

    const handleMouseOut = (e: MouseEvent) => {
      const target = e.target as Element;
      if (target === lastHoveredElement) {
        if (hoverTimeout) {
          clearTimeout(hoverTimeout);
          hoverTimeout = null;
        }
        lastHoveredElement = null;
      }
    };

    // Mouse movement heatmap tracking (throttled)
    let lastHeatmapSent = 0;
    const HEATMAP_THROTTLE_MS = 200;
    const handleMouseMove = (e: MouseEvent) => {
      const now = Date.now();
      if (now - lastHeatmapSent > HEATMAP_THROTTLE_MS) {
        lastHeatmapSent = now;
        captureEvent('mouse_heatmap', {
          session_id: sessionId,
          x: e.clientX,
          y: e.clientY,
          timestamp: new Date().toISOString(),
        });
      }
    };

    document.addEventListener('mouseover', handleMouseOver, true);
    document.addEventListener('mouseout', handleMouseOut, true);
    document.addEventListener('mousemove', handleMouseMove, true);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('mouseover', handleMouseOver, true);
      document.removeEventListener('mouseout', handleMouseOut, true);
      document.removeEventListener('mousemove', handleMouseMove, true);
    };
  }, []);

  return (
    <Router>
      <LanguageInitializer />
      <Routes>
        <Route path="/" element={<Index />} />
        <Route path="/success" element={<Success />} />
        <Route path="/demo" element={<Demo />} />
        <Route path="/brand-setup/:niche" element={<BrandSetup />} />
      </Routes>
    </Router>
  );
}

export default App;
